import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayout } from '../components/layout/SharedLayout';
import { RESEND_CONFIG } from '../config/resend.config';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: calc(80px + 60px) 20px 60px 20px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: calc(80px + 40px) 20px 40px 20px;
  }
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 60px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSection = styled.div``;

const Badge = styled.div`
  background: #ff4444;
  color: white;
  padding: 8px 20px;
  display: inline-block;
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 20px;
  letter-spacing: 2px;
`;

const Title = styled.h1`
  font-size: 48px;
  color: ${theme.colors.primary.dark};
  margin-bottom: 20px;
  font-weight: 900;
  line-height: 1.1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 36px;
  }
`;

const Subtitle = styled.h2`
  font-size: 28px;
  color: ${theme.colors.primary.dark};
  margin-bottom: 25px;
  font-weight: 400;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 22px;
  }
`;

const PriceText = styled.div`
  font-size: 24px;
  margin-bottom: 30px;
  
  span.strike {
    text-decoration: line-through;
    color: #ff4444;
    margin-right: 10px;
  }
  
  span.free {
    font-weight: bold;
    color: #00aa00;
  }
  
  span.limited {
    color: #666;
    font-size: 18px;
    font-style: italic;
  }
`;

const FormContainer = styled.div`
  background: #f7fafc;
  padding: 40px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px 20px;
  }
`;

const FormTitle = styled.h3`
  font-size: 24px;
  color: ${theme.colors.primary.dark};
  margin-bottom: 25px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.primary.dark};
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: #ffd700;
  color: ${theme.colors.primary.dark};
  padding: 16px 32px;
  font-size: 20px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 10px;
  
  &:hover:not(:disabled) {
    background: #ffcc00;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(255, 215, 0, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const BookImageSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BookImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #00aa00, #00cc00);
  color: white;
  padding: 25px;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(0, 170, 0, 0.2);
  animation: slideInUp 0.5s ease-out;
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  h4 {
    font-size: 22px;
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 16px;
    line-height: 1.5;
    margin: 5px 0;
  }
`;

const ErrorMessage = styled.div`
  background: #ff4444;
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
  margin-bottom: 20px;
`;

const BenefitsSection = styled.section`
  background: #f7fafc;
  padding: 60px 40px;
  border-radius: 12px;
  margin-top: 60px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 40px 20px;
  }
`;

const BenefitsTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.primary.dark};
  text-align: center;
  margin-bottom: 40px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 800px;
  margin: 0 auto;
`;

const BenefitItem = styled.li`
  font-size: 18px;
  padding: 15px 0;
  padding-left: 40px;
  position: relative;
  color: ${theme.colors.primary.dark};
  border-bottom: 1px solid #e2e8f0;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #00aa00;
    font-size: 24px;
    font-weight: bold;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

export const EbookDownload: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [successEmail, setSuccessEmail] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Submit form to email API
      const response = await fetch(RESEND_CONFIG.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          data: {
            to_email: 'jake@averillagencies.com',
            from_name: `${formData.firstName} ${formData.lastName}`,
            from_email: formData.email,
            phone: formData.phone || 'Not provided',
            insurance_company: 'EBOOK DOWNLOAD',
            annuity_product: 'Demystifying Annuities',
            year_purchased: new Date().getFullYear().toString(),
            current_age: 'Lead Generation',
            message: `
🎯 NEW EBOOK DOWNLOAD LEAD 📚

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
IMMEDIATE ACTION REQUIRED - HOT LEAD
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Lead Details:
• Name: ${formData.firstName} ${formData.lastName}
• Email: ${formData.email}
• Phone: ${formData.phone || '❌ Not provided - EMAIL ONLY'}
• Downloaded: "Demystifying Annuities" eBook
• Date/Time: ${new Date().toLocaleString()}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📋 RECOMMENDED FOLLOW-UP SEQUENCE:

Day 1 (Today):
✓ eBook automatically delivered via website
□ Send welcome email with additional resources

Day 2-3:
□ Follow-up call if phone provided
□ "Did you receive the ebook?" email check-in

Day 7:
□ Send case study or success story
□ Offer free consultation

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Lead Score: 🔥 HIGH INTEREST
Reason: Actively seeking annuity education

Note: This lead has shown interest by downloading educational content. They are likely in the research phase and could be ready for a consultation.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            `.trim()
          }
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setSuccessEmail(formData.email); // Store email before clearing form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: ''
        });
        
        // Automatically download the PDF
        const downloadLink = document.createElement('a');
        downloadLink.href = '/Annuity Answers eBook.pdf';
        downloadLink.download = 'Annuity Answers eBook.pdf';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        // Auto-hide success message after 10 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
          setSuccessEmail('');
        }, 10000);
      } else {
        // Handle API errors
        if (data.error) {
          throw new Error(data.error);
        } else {
          throw new Error('Failed to submit form. Please check your information and try again.');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      
      if (error instanceof Error) {
        // Check for network errors
        if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
          setErrorMessage('Network error. Please check your connection and try again, or call us at 800-922-1894.');
        } else {
          setErrorMessage(error.message || 'Something went wrong. Please try again or call us at 800-922-1894.');
        }
      } else {
        setErrorMessage('Something went wrong. Please try again or call us at 800-922-1894.');
      }
      
      // Auto-hide error message after 7 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        setErrorMessage('');
      }, 7000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedLayout>
      <PageContainer>
        <HeroSection>
          <ContentSection>
            <Badge>— FREE EBOOK —</Badge>
            <Title>DEMYSTIFYING ANNUITIES</Title>
            <Subtitle>How AI Is Changing The Industry</Subtitle>
            <PriceText>
              <span className="strike">$19.99</span>
              <span className="free">NOW FREE</span> <span className="limited">(Limited Time Only)</span>
            </PriceText>
            
            <FormContainer>
              <FormTitle>Get Your Free Copy Instantly</FormTitle>
              {submitStatus === 'success' && (
                <SuccessMessage>
                  <h4>Success! Your eBook is downloading now!</h4>
                  <p>Thank you for your interest in "Demystifying Annuities"!</p>
                  <p><strong>Your download should start automatically.</strong></p>
                  <p>If it doesn't, <a href="/Annuity Answers eBook.pdf" download style={{ color: 'white', textDecoration: 'underline' }}>click here to download</a></p>
                  <p style={{ marginTop: '10px' }}>We've also sent a copy to <strong>{successEmail}</strong></p>
                  <p>One of our specialists will reach out within 24-48 hours to see if you have any questions</p>
                  <p style={{ fontSize: '14px', marginTop: '15px', opacity: 0.9 }}>
                    Need help? Call us at 800-922-1894
                  </p>
                </SuccessMessage>
              )}
              {submitStatus === 'error' && (
                <ErrorMessage>
                  {errorMessage}
                </ErrorMessage>
              )}
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </FormGroup>
                
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get Your Free Ebook'}
                </SubmitButton>
              </Form>
            </FormContainer>
          </ContentSection>
          
          <BookImageSection>
            <BookImage src="/ebook-cover.png" alt="Demystifying Annuities Ebook Cover" />
          </BookImageSection>
        </HeroSection>
        
        <BenefitsSection>
          <BenefitsTitle>What You'll Learn</BenefitsTitle>
          <BenefitsList>
            <BenefitItem>Over 50 years of annuity expertise distilled into one comprehensive guide</BenefitItem>
            <BenefitItem>How artificial intelligence is revolutionizing the annuity industry</BenefitItem>
            <BenefitItem>Understanding different types of annuities and which might be right for you</BenefitItem>
            <BenefitItem>Common misconceptions about annuities debunked</BenefitItem>
            <BenefitItem>The Averill Method: Our proven approach to retirement planning</BenefitItem>
            <BenefitItem>Real-world case studies from over 10,000 satisfied clients</BenefitItem>
            <BenefitItem>How to protect your retirement from market volatility</BenefitItem>
            <BenefitItem>Expert insights on maximizing your retirement income</BenefitItem>
          </BenefitsList>
        </BenefitsSection>
      </PageContainer>
    </SharedLayout>
  );
};